import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react'

import { useMediaQuery } from '../utils/useMediaQuery'
import { syncDarkModeCookie, updateDarkModeStyles } from './darkModeUtils'
import styles from './styles.module.scss'
import { themeSupportsDarkMode } from './themeCapabilities'
import { DarkMode, DesignLanguage, Theme, ThemeProps, ThemeProviderConfig } from './types'

const defaultContext: ThemeProps = {
  theme: {
    selected: Theme.AP,
    darkMode: DarkMode.System,
    isDarkModeActive: false,
    setTheme: () => {},
    setDarkMode: () => {},
    designLanguage: DesignLanguage.Bronte,
  },
}

const DESIGN_LANGUAGE_BY_THEME: Record<Theme, DesignLanguage> = {
  [Theme.AP]: DesignLanguage.Bronte,
  [Theme.Pegasus]: DesignLanguage.Arcade,
  [Theme.Kelpie]: DesignLanguage.Arcade,
  [Theme.KelpieMint]: DesignLanguage.Arcade,
  [Theme.Cash]: DesignLanguage.Arcade,
}

export const ThemeContext = createContext<ThemeProps>(defaultContext)

export const ThemeProvider: React.FC<ThemeProviderConfig> = ({ children, theme = Theme.AP, darkMode, getDarkModePreference, setDarkModePreference }) => {
  const isPrefersDark = useMediaQuery('(prefers-color-scheme: dark)')

  const getInitialDarkMode = (): DarkMode => {
    if (darkMode !== undefined) {
      return darkMode
    }

    if (getDarkModePreference) {
      const preference = getDarkModePreference()

      if (preference === 'dark') return DarkMode.Dark
      if (preference === 'light') return DarkMode.Light
      if (preference === 'system') return DarkMode.System
    }

    return DarkMode.System
  }

  const [isDarkMode, setDarkModeState] = useState(getInitialDarkMode())
  const [selectedTheme, setSelectedTheme] = useState(theme)

  useEffect(() => {
    if (typeof window === 'undefined' || typeof getDarkModePreference !== 'function') return

    const preference = getDarkModePreference()
    if (preference === 'system') {
      setDarkModeState(DarkMode.System)
    }
  }, [getDarkModePreference])

  const setDarkMode = useCallback(
    (newDarkMode: DarkMode) => {
      setDarkModeState(newDarkMode)

      if (typeof window !== 'undefined') {
        syncDarkModeCookie(newDarkMode, setDarkModePreference)
      }
    },
    [setDarkModePreference]
  )

  const isDarkModeActive = isDarkMode === DarkMode.System ? isPrefersDark ?? false : isDarkMode === DarkMode.Dark

  const context = useMemo(
    () => ({
      theme: {
        selected: selectedTheme,
        darkMode: isDarkMode,
        isDarkModeActive,
        setDarkMode,
        setTheme: (theme: Theme) => setSelectedTheme(theme),
        designLanguage: DESIGN_LANGUAGE_BY_THEME[selectedTheme],
      },
    }),
    [selectedTheme, isDarkMode, isDarkModeActive, setDarkMode]
  )

  useEffect(() => {
    if (typeof window === 'undefined') return
    document.documentElement.setAttribute('data-theme', selectedTheme)
  }, [selectedTheme])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const calculatedDarkMode = isDarkMode === DarkMode.System ? isPrefersDark ?? false : isDarkMode === DarkMode.Dark

    if (themeSupportsDarkMode(selectedTheme)) {
      updateDarkModeStyles(calculatedDarkMode, true)
    } else {
      updateDarkModeStyles(false, false)
    }
  }, [selectedTheme, isDarkMode, isPrefersDark])

  return (
    <ThemeContext.Provider value={context}>
      <div className={styles.container}>{children}</div>
    </ThemeContext.Provider>
  )
}
